/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import StateDependencyComponent from '../StateManager/StateDependencyComponent';
import CLog, { LogTypes } from './ComponentLogger';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/** Responsible for re-cropping the canvas between landscape and portrait mode. */
@cc._decorator.ccclass
@cc._decorator.requireComponent(cc.Canvas)
@cc._decorator.executeInEditMode
export default class AutoResizingCanvas extends StateDependencyComponent
{
    /** Whether instance of component is injectable. */
    @property({ visible: true, override: true })
    protected _isActive: boolean = true;

    /** Design size to use while in portrait mode. */
    @property({ visible: true })
    private _portraitSize: cc.Size = cc.Size.ZERO;
    public get PortraitSize(): cc.Size { return this._portraitSize; }

    /** Design size to use while in landscape mode. */
    @property({ visible: true })
    private _landscapeSize: cc.Size = cc.Size.ZERO;
    public get LandscapeSize(): cc.Size { return this._landscapeSize; }

    /** Whether current canvas orientation is landscape. */
    protected isLandscape: boolean;
    public get IsLandScape(): boolean { return this.isLandscape; }

    /** @hidden */
    private static readonly OrientationChangeEvent: string = "onOrientationChange";

    /** @hidden */
    private _onOrientationChangeDispatcher: cc.EventTarget = new cc.EventTarget();
    /** @hidden */
    private _lastFrameSize: cc.Size = cc.Size.ZERO;
    /** @hidden */
    private _currentFrameSize: cc.Size = cc.Size.ZERO;
    /** @hidden */
    private _updateScheduled: boolean = false;
    /** @hidden */
    private _waitOneFrame: boolean = true;

    public IsDesignTimeLandscape(): boolean | null
    {
        const canvas: cc.Canvas = this.getComponent(cc.Canvas);

        if (!canvas || !CC_EDITOR)
        {
            CLog.error('Could not find canvas component.', this, LogTypes.Editor);
            return null;
        }
        return canvas.designResolution.equals(this._landscapeSize);
    }

    /** @hidden */
    protected update(dt: number): void
    {
        if (!CC_EDITOR)
        {
            super.update(dt);

            if (this._waitOneFrame)
            {
                this._waitOneFrame = false;
                return;
            }

            this.canvasUpdate();

            this._lastFrameSize = this._currentFrameSize;
        }
    }

    protected canvasUpdate(): void
    {
        let updateContentSize = false;
        if (cc.sys.isMobile && cc.sys.BROWSER_TYPE_SAFARI)
        {
            this._currentFrameSize = this.iosFrame;
            updateContentSize = true;
        }
        else if (CC_JSB)
        {
            this._currentFrameSize = this.nativeFrame;
        }
        else
        {
            this._currentFrameSize = this.defaultFrame;
        }

        if (this._currentFrameSize.width !== this._lastFrameSize.width ||
            this._currentFrameSize.height !== this._lastFrameSize.height)
        {
            if (updateContentSize)
            {
                let contentDiv: HTMLElement = document.getElementById("content");
                contentDiv.style.width = this._currentFrameSize.width + "px";
                contentDiv.style.height = this._currentFrameSize.height + "px";
                window.scrollTo(0, 0);
            }

            if (!this._updateScheduled)
            {
                let frameType: string = (cc.sys.isMobile && cc.sys.BROWSER_TYPE_SAFARI) ? "iOS" : "Default";
                CLog.log(`${frameType} frame change detected. CurrentFrame: ${this._currentFrameSize}`
                    + ` LastFrame: ${this._lastFrameSize}`, LogTypes.Utility, this);
                this.scheduleOnce(this.adjustDesignSize, 0.25);
                this._updateScheduled = true;
            }
        }
    }

    private get nativeFrame(): cc.Size { return cc.director.getWinSizeInPixels(); }
    private get defaultFrame(): cc.Size { return cc.view.getFrameSize(); }
    private get iosFrame(): cc.Size { return new cc.Size(window.innerWidth, window.innerHeight); }

    /**
     * Register a function to be called after the orientation has changed and the design size has been updated.
     * @param handler The function to register.
     */
    public AddOnOrientationChangeHandler(handler: () => void): void
    {
        this._onOrientationChangeDispatcher.on(AutoResizingCanvas.OrientationChangeEvent, handler);
    }

    /**
     * Deregister a function that should no longer be called when the orientation changes.
     * @param handler The function to unregister.
     */
    public RemoveOrientationChangeHandler(handler: () => void): void
    {
        this._onOrientationChangeDispatcher.off(AutoResizingCanvas.OrientationChangeEvent, handler);
    }

    /** @hidden */
    private adjustDesignSize = (frame: cc.Size = cc.Size.ZERO): void =>
    {
        if (frame === cc.Size.ZERO || !(frame.width && frame.height))
        {
            frame = this._currentFrameSize;
        }
        this.isLandscape = frame.width > frame.height;
        let designSize: cc.Size = this.isLandscape ? this._landscapeSize : this._portraitSize;
        let resolutionPolicy: number = cc.ResolutionPolicy.SHOW_ALL;

        cc.view.setDesignResolutionSize(designSize.width, designSize.height, resolutionPolicy);
        this._onOrientationChangeDispatcher.emit(AutoResizingCanvas.OrientationChangeEvent);
        this._updateScheduled = false;
    }
}
